import type { BaseReactProps, ReactNodeDef } from '@shared/node-v1.0.0'
import { getPortDef, sizes } from '@shared/port-v1.0.0'
import { lazy } from 'react'

export type Props = BaseReactProps & {
	loading: boolean
	disabled: boolean
	dropZoneTitle?: string
	titleAlign?: 'left' | 'center' | 'right' | 'justify'
	acceptedTypeAll: boolean
	acceptedTypePDF: boolean
	acceptedTypeTABLE: boolean
	acceptedTypeDOCS: boolean
	acceptedTypeIMAGES: boolean
	acceptedTypeVIDEO: boolean
	acceptedTypeAUDIO: boolean
	acceptedTypeARCHIVES: boolean
	maxSize: number
	getOneFile: boolean
	acceptIconName: string
	rejectIconName: string
	idleIconName: string
	iconSize?: string
	stroke?: number
	borderWidth: number
	borderType: 'solid' | 'dashed' | 'dotted' | 'double'
	borderColor: 'string'
	borderRadius: 'string'
}

export default {
	hashTag: '#experimental',
	module: { dynamic: lazy(() => import('../component')) },
	inNode: {
		inputs: [
			getPortDef({ name: 'loading', displayName: 'Loading', group: 'States', type: 'boolean', default: false }),
			getPortDef({ name: 'disabled', displayName: 'Disabled', group: 'States', type: 'boolean', default: false }),
			getPortDef({
				name: 'dropZoneTitle',
				displayName: 'Title',
				group: 'Styles',
				type: 'string',
				default: 'Внесите файл или нажмите, чтобы открыть в папке.',
			}),
			getPortDef({
				name: 'titleAlign',
				displayName: 'Title align',
				group: 'Styles',
				type: [
					{ label: 'left', value: 'left' },
					{ label: 'center', value: 'center' },
					{ label: 'right', value: 'right' },
					{ label: 'justify', value: 'justify' },
				],
				default: 'center',
			}),

			// getPortDef({
			// 	name: 'acceptedType',
			// 	displayName: 'Type of accepted file',
			// 	group: 'Params',
			// 	type: 'string',
			// 	default: '*',
			// 	tooltip: 'Типы нужно писать через "," и с точкой, наприме: ".pdf, .png"',
			// }),

			// Типы файлов
			getPortDef({
				name: 'acceptedTypeAll',
				displayName: 'Get all types',
				group: 'Custom',
				customGroup: 'File types',
				type: 'boolean',
				default: true,
			}),
			getPortDef({
				name: 'acceptedTypePDF',
				displayName: 'PDF',
				group: 'Custom',
				customGroup: 'File types',
				type: 'boolean',
				default: true,
				dependsOn: (p: Props) => !p.acceptedTypeAll,
			}),
			getPortDef({
				name: 'acceptedTypeTABLE',
				displayName: 'Table',
				group: 'Custom',
				customGroup: 'File types',
				type: 'boolean',
				default: true,
				dependsOn: (p: Props) => !p.acceptedTypeAll,
			}),
			getPortDef({
				name: 'acceptedTypeDOCS',
				displayName: 'Docs',
				group: 'Custom',
				customGroup: 'File types',
				type: 'boolean',
				default: true,
				dependsOn: (p: Props) => !p.acceptedTypeAll,
			}),
			getPortDef({
				name: 'acceptedTypeIMAGES',
				displayName: 'Images',
				group: 'Custom',
				customGroup: 'File types',
				type: 'boolean',
				default: true,
				dependsOn: (p: Props) => !p.acceptedTypeAll,
			}),
			getPortDef({
				name: 'acceptedTypeVIDEO',
				displayName: 'Video',
				group: 'Custom',
				customGroup: 'File types',
				type: 'boolean',
				default: true,
				dependsOn: (p: Props) => !p.acceptedTypeAll,
			}),
			getPortDef({
				name: 'acceptedTypeAUDIO',
				displayName: 'Audio',
				group: 'Custom',
				customGroup: 'File types',
				type: 'boolean',
				default: true,
				dependsOn: (p: Props) => !p.acceptedTypeAll,
			}),
			getPortDef({
				name: 'acceptedTypeARCHIVES',
				displayName: 'Archives',
				group: 'Custom',
				customGroup: 'File types',
				type: 'boolean',
				default: true,
				dependsOn: (p: Props) => !p.acceptedTypeAll,
			}),

			getPortDef({
				name: 'maxSize',
				displayName: 'Maximum file size',
				group: 'Params',
				type: 'number',
				default: 20,
				tooltip: 'Максимальный размер файла.',
			}),
			getPortDef({
				name: 'getOneFile',
				displayName: 'Get one file',
				group: 'Params',
				type: 'boolean',
				default: false,
				tooltip: 'Если true, то можно будет выбрать или внести только 1 файл!',
			}),
			getPortDef({
				name: 'acceptIconName',
				displayName: 'Accept icon name',
				group: 'Custom',
				customGroup: 'Icons',
				type: 'string',
				default: 'IconDownload',
			}),
			getPortDef({
				name: 'rejectIconName',
				displayName: 'Reject icon name',
				group: 'Custom',
				customGroup: 'Icons',
				type: 'string',
				default: 'IconX',
			}),
			getPortDef({
				name: 'idleIconName',
				displayName: 'Neutral icon name',
				group: 'Custom',
				customGroup: 'Icons',
				type: 'string',
				default: 'IconFolder',
			}),
			getPortDef({
				name: 'iconSize',
				displayName: 'Size',
				group: 'Custom',
				customGroup: 'Icons',
				type: 'number',
				default: 24,
			}),
			getPortDef({
				name: 'stroke',
				displayName: 'Stroke', // Толщина линии иконок
				group: 'Custom',
				customGroup: 'Icons',
				type: 'number',
				default: 1,
			}),

			// Рамка
			getPortDef({
				name: 'borderWidth',
				displayName: 'Width', // Толщина линии рамки
				group: 'Custom',
				customGroup: 'Border',
				type: 'number',
				default: 1,
			}),
			getPortDef({
				name: 'borderType',
				displayName: 'Type', // Тип линии рамки
				group: 'Custom',
				customGroup: 'Border',
				type: [
					{ label: 'solid', value: 'solid' },
					{ label: 'dashed', value: 'dashed' },
					{ label: 'dotted', value: 'dotted' },
					{ label: 'double', value: 'double' },
				],
				default: 'dotted',
			}),
			getPortDef({
				name: 'borderColor',
				displayName: 'Color', // Цвет линии рамки
				group: 'Custom',
				customGroup: 'Border',
				type: 'string',
				default: 'gray',
			}),
			getPortDef({
				name: 'borderRadius',
				displayName: 'Radius',
				group: 'Custom',
				customGroup: 'Border',
				type: sizes,
				default: 'sm',
			}),
		],
		outputs: [
			getPortDef({
				name: 'loaded',
				displayName: 'Loaded',
				group: 'Signals',
				type: 'signal',
			}),
			getPortDef({
				name: 'rejected',
				displayName: 'Rejected',
				group: 'Signals',
				type: 'signal',
			}),
			getPortDef({
				name: 'files',
				displayName: 'Files',
				group: 'Data',
				type: 'array',
			}),
		],
	},
} satisfies ReactNodeDef
